import { AuthActions, AuthActionTypes } from '@store/actions/auth.actions';
import * as fromRouter from '@ngrx/router-store';
import {
    ActionReducer,
    ActionReducerMap,
    createFeatureSelector,
    createSelector,
    MetaReducer
} from '@ngrx/store';
import { RouterStateUrl } from '@shared/utils/routerUtil';
import * as fromAuth from '@store/reducers/auth.reducers';
import * as fromPulseType from '@store/reducers/pulse-type.reducers';
import * as fromSpinner from '@store/reducers/spinner.reducers';
import * as fromUser from '@store/reducers/user.reducers';
import { environment } from 'environments/environment';
import { storeFreeze } from 'ngrx-store-freeze';
import { localStorageSync } from 'ngrx-store-localstorage';
import { Logout } from '@store/actions';

/**
 * As mentioned, we treat each reducer like a table in a database. This means
 * our top level state interface is just a map of keys to inner state types.
 */
export interface State {
    router: fromRouter.RouterReducerState<RouterStateUrl>;
    spinner: fromSpinner.SpinnerState;
    auth: fromAuth.AuthState;
    user: fromUser.UserState;
    pulseType: fromPulseType.PulseTypeState;
}

/**
 * Our state is composed of a map of action reducer functions.
 * These reducer functions are called with each dispatched action
 * and the current or initial state and return a new immutable state.
 */
export const reducers: ActionReducerMap<State> = {
    router: fromRouter.routerReducer,
    spinner: fromSpinner.reducer,
    auth: fromAuth.reducer,
    user: fromUser.reducer,
    pulseType: fromPulseType.reducer
};
export function resetReducer(
    reducer: ActionReducer<State>
): ActionReducer<State> {
    return function (state: State, action: any): State {
        if (action.type === AuthActionTypes.Logout) {
            state = undefined;
        }

        return reducer(state, action);
    };
}
// console.log all actions
// tslint:disable-next-line:no-shadowed-variable

export function logger(reducer: ActionReducer<State>): ActionReducer<State> {
    return function (state: State, action: any): State {
        console.log('state', state);
        console.log('action', action);

        return reducer(state, action);
    };
}

/**
 * By default, @ngrx/store uses combineReducers with the reducer map to compose
 * the root meta-reducer. To add more meta-reducers, provide an array of meta-reducers
 * that will be composed to form the root meta-reducer.
 */
export const metaReducers: MetaReducer<State>[] = !environment.production
    ? [logger, storeFreeze, localStorageSyncReducer, resetReducer]
    : [localStorageSyncReducer, resetReducer];

export function localStorageSyncReducer(
    reducer: ActionReducer<any>
): ActionReducer<any> {
    return localStorageSync({
        keys: ['auth', 'user'],
        rehydrate: true,
        storage: sessionStorage
    })(reducer);
}

export const getRouterState = createFeatureSelector<
    fromRouter.RouterReducerState<RouterStateUrl>
>('router');
export const getSpinnerState = createFeatureSelector<fromSpinner.SpinnerState>(
    'spinner'
);
export const getAuthState = createFeatureSelector<fromAuth.AuthState>('auth');
export const getUserState = createFeatureSelector<fromUser.UserState>('user');
export const getPulseTypeState = createFeatureSelector<
    fromPulseType.PulseTypeState
>('pulseType');



export const getIsLoading = createSelector(
    getSpinnerState,
    fromSpinner.isShowing
);

export const getPrivacyPolicyContent = createSelector(
    getAuthState,
    fromAuth.getPrivacyPolicyContent
);

export const getForgotPasswordResult = createSelector(
    getAuthState,
    fromAuth.getForgotPasswordResult
);

export const getValidateResetPasswordCodeResult = createSelector(
    getAuthState,
    fromAuth.getValidateResetPasswordCodeResult
);

export const getIsLoggedIn = createSelector(
    getAuthState,
    fromAuth.getIsLoggedIn
);
export const getAuthData = createSelector(getAuthState, fromAuth.getAuthData);

export const getCurrentUrl = createSelector(
    getRouterState,
    (state: fromRouter.RouterReducerState<RouterStateUrl>) =>
        state && state.state && state.state.url
);

export const getLoggedinUserAndRoleInfo = createSelector(
    getUserState,
    fromUser.getLoggedinUserAndRoleInfo
);
export const getHowHeardAboutTypes = createSelector(
    getPulseTypeState,
    fromPulseType.getHowHeardAboutTypes
);
export const getCountryTypes = createSelector(
    getPulseTypeState,
    fromPulseType.getCountryTypes
);

export const getSuffixTypes = createSelector(
    getPulseTypeState,
    fromPulseType.getSuffixTypes
);
export const getPhoneTypes = createSelector(
    getPulseTypeState,
    fromPulseType.getPhoneTypes
);
export const getUSStateTypes = createSelector(
    getPulseTypeState,
    fromPulseType.getUSStateTypes
);
export const GetCanadaProvinceTypes = createSelector(
    getPulseTypeState,
    fromPulseType.getCanadaProvinceTypes
);
export const GetContactMethodTypes = createSelector(
    getPulseTypeState,
    fromPulseType.getContactMethodTypes
);
export const GetTimeZoneTypes = createSelector(
    getPulseTypeState,
    fromPulseType.getTimeZoneTypes
);
export const getMetroAreaTypes = createSelector(
    getPulseTypeState,
    fromPulseType.getMetroAreaTypes
);
export const getSchoolDegreeTypes = createSelector(
    getPulseTypeState,
    fromPulseType.getSchoolDegreeTypes
);
